/**
 * Metadata Extractor: Import/Export/Symbol Analysis
 *
 * Extracts comprehensive metadata from code chunks:
 * - Import statements (with workspace classification for monorepos)
 * - Export statements
 * - Symbol definitions (functions, classes, variables, types)
 * - Cyclomatic complexity
 * - Dependency analysis
 * - API endpoint patterns (for microservices)
 */

import { type ImportsData, type ImportType } from '@/types/database';
import { NodeType, type APIEndpointInfo, type ChunkMetadata, type ParseResult } from '@/types/indexing';

/**
 * Workspace import patterns for monorepo detection
 */
const WORKSPACE_IMPORT_PATTERNS = [
  /^@workspace\//,
  /^@[a-z0-9-]+\//, // Scoped packages (@myorg/package)
  /^@\//, // TypeScript path alias
  /^~\//, // Alternative path alias
];

/**
 * Metadata extractor for code analysis
 */
export class MetadataExtractor {
  /**
   * Convert parse result imports to structured database format
   *
   * Transforms ImportInfo[] from the parser to ImportsData for database storage,
   * classifying each import by type (external, workspace, relative, absolute).
   *
   * @param parseResult - Parse result with import information
   * @returns Structured imports data with line numbers and classification
   */
  public convertImportsToStructuredFormat = (parseResult: ParseResult): ImportsData => {
    const imports = parseResult.imports.map((imp) => {
      // Classify import type
      const importType = this.classifyImportType(imp.source);

      return {
        path: imp.source,
        line: imp.line_number,
        symbols: imp.is_namespace && imp.namespace_alias ? [imp.namespace_alias] : imp.symbols,
        type: importType,
      };
    });

    return { imports };
  };

  /**
   * Classify import type based on import path
   *
   * @param importPath - Import source path
   * @returns Import type classification
   */
  private classifyImportType = (importPath: string): ImportType => {
    // Workspace imports: @workspace/, @orgname/, @/, ~/
    if (WORKSPACE_IMPORT_PATTERNS.some((pattern) => pattern.test(importPath))) {
      return 'workspace';
    }

    // Relative imports: ./ or ../
    if (importPath.startsWith('./') || importPath.startsWith('../')) {
      return 'relative';
    }

    // Absolute imports: / prefix (rare but possible)
    if (importPath.startsWith('/')) {
      return 'absolute';
    }

    // External imports: node_modules packages
    return 'external';
  };
  /**
   * Extract comprehensive metadata from parsed code
   *
   * @param parseResult - Parse result with nodes, imports, exports
   * @param code - Raw code content
   * @returns Chunk metadata with symbols, imports, exports, complexity
   */
  public extractMetadata = (parseResult: ParseResult, code: string): ChunkMetadata => {
    // Extract function names
    const functionNames = this.extractFunctionNames(parseResult);

    // Extract class names
    const classNames = this.extractClassNames(parseResult);

    // Extract imported symbols
    const importedSymbols = this.extractImportedSymbols(parseResult);

    // Extract exported symbols
    const exportedSymbols = this.extractExportedSymbols(parseResult);

    // Extract dependencies (module paths from imports)
    const dependencies = this.extractDependencies(parseResult);

    // Calculate total cyclomatic complexity
    const complexity = this.calculateTotalComplexity(parseResult);

    // Detect async/await usage
    const hasAsync = code.includes('async') || code.includes('await');

    // Detect loops
    const hasLoops = this.detectLoops(code);

    // Detect conditionals
    const hasConditionals = this.detectConditionals(code);

    // Check for internal workspace imports (monorepo)
    const isInternalImport = this.detectInternalImports(parseResult);

    // Extract API endpoints (microservices)
    const apiEndpoints = this.extractAPIEndpoints(code);

    const metadata: ChunkMetadata = {
      function_names: functionNames,
      class_names: classNames,
      imported_symbols: importedSymbols,
      exported_symbols: exportedSymbols,
      dependencies,
      complexity,
      has_async: hasAsync,
      has_loops: hasLoops,
      has_conditionals: hasConditionals,
    };

    // Add optional fields
    if (isInternalImport !== null) {
      metadata.is_internal_import = isInternalImport;
    }

    if (apiEndpoints.length > 0) {
      metadata.api_endpoints = apiEndpoints;
    }

    return metadata;
  };

  /**
   * Extract function names from parse result
   */
  private extractFunctionNames = (parseResult: ParseResult): string[] => {
    return parseResult.nodes
      .filter((node) => node.node_type === NodeType.Function || node.node_type === NodeType.Method)
      .map((node) => node.name)
      .filter((name) => name !== '<anonymous>');
  };

  /**
   * Extract class names from parse result
   */
  private extractClassNames = (parseResult: ParseResult): string[] => {
    return parseResult.nodes
      .filter((node) => node.node_type === NodeType.Class)
      .map((node) => node.name)
      .filter((name) => name !== '<anonymous>');
  };

  /**
   * Extract imported symbols from parse result
   */
  private extractImportedSymbols = (parseResult: ParseResult): string[] => {
    const symbols: string[] = [];

    for (const imp of parseResult.imports) {
      if (imp.is_namespace && imp.namespace_alias) {
        symbols.push(imp.namespace_alias);
      } else {
        symbols.push(...imp.symbols);
      }
    }

    return symbols;
  };

  /**
   * Extract exported symbols from parse result
   */
  private extractExportedSymbols = (parseResult: ParseResult): string[] => {
    const symbols: string[] = [];

    for (const exp of parseResult.exports) {
      symbols.push(...exp.symbols);
    }

    return symbols;
  };

  /**
   * Extract dependencies (imported module paths)
   */
  private extractDependencies = (parseResult: ParseResult): string[] => {
    return parseResult.imports.map((imp) => imp.source).filter((source) => source !== '');
  };

  /**
   * Calculate total cyclomatic complexity for all functions
   */
  private calculateTotalComplexity = (parseResult: ParseResult): number => {
    let totalComplexity = 0;

    for (const node of parseResult.nodes) {
      if (node.complexity) {
        totalComplexity += node.complexity;
      }
    }

    return totalComplexity || 1; // Minimum complexity is 1
  };

  /**
   * Detect loop statements in code
   */
  private detectLoops = (code: string): boolean => {
    const loopPatterns = [
      /\bfor\s*\(/,
      /\bwhile\s*\(/,
      /\bdo\s*{/,
      /\.forEach\(/,
      /\.map\(/,
      /\.filter\(/,
      /\.reduce\(/,
    ];

    return loopPatterns.some((pattern) => pattern.test(code));
  };

  /**
   * Detect conditional statements in code
   */
  private detectConditionals = (code: string): boolean => {
    const conditionalPatterns = [/\bif\s*\(/, /\belse\b/, /\bswitch\s*\(/, /\?\s*.*\s*:/];

    return conditionalPatterns.some((pattern) => pattern.test(code));
  };

  /**
   * Detect internal workspace imports (monorepo)
   *
   * Returns null if no imports, true if any internal imports, false if all external
   */
  private detectInternalImports = (parseResult: ParseResult): boolean | null => {
    if (parseResult.imports.length === 0) {
      return null;
    }

    for (const imp of parseResult.imports) {
      // Check if import matches workspace patterns
      if (WORKSPACE_IMPORT_PATTERNS.some((pattern) => pattern.test(imp.source))) {
        return true;
      }

      // Check for relative imports (internal to project)
      if (imp.source.startsWith('./') || imp.source.startsWith('../')) {
        return true;
      }
    }

    return false;
  };

  /**
   * Extract API endpoint patterns from code (microservices)
   *
   * Detects:
   * - Express routes: app.get('/api/users', ...)
   * - NestJS decorators: @Get('/users')
   * - GraphQL resolvers: @Query() or @Mutation()
   * - gRPC service definitions: service UserService { rpc GetUser ... }
   */
  private extractAPIEndpoints = (code: string): APIEndpointInfo[] => {
    const endpoints: APIEndpointInfo[] = [];

    // Express.js routes
    const expressPatterns = [
      { method: 'GET', regex: /app\.get\s*\(\s*['"]([^'"]+)['"]/g },
      { method: 'POST', regex: /app\.post\s*\(\s*['"]([^'"]+)['"]/g },
      { method: 'PUT', regex: /app\.put\s*\(\s*['"]([^'"]+)['"]/g },
      { method: 'DELETE', regex: /app\.delete\s*\(\s*['"]([^'"]+)['"]/g },
      { method: 'PATCH', regex: /app\.patch\s*\(\s*['"]([^'"]+)['"]/g },
    ];

    for (const { method, regex } of expressPatterns) {
      let match;
      while ((match = regex.exec(code)) !== null) {
        const path = match[1];
        const lineNumber = code.substring(0, match.index).split('\n').length;

        endpoints.push({
          method,
          path,
          line_number: lineNumber,
          api_type: 'rest',
        });
      }
    }

    // NestJS decorators
    const nestJSPatterns = [
      { method: 'GET', regex: /@Get\s*\(\s*['"]([^'"]+)['"]\s*\)/g },
      { method: 'POST', regex: /@Post\s*\(\s*['"]([^'"]+)['"]\s*\)/g },
      { method: 'PUT', regex: /@Put\s*\(\s*['"]([^'"]+)['"]\s*\)/g },
      { method: 'DELETE', regex: /@Delete\s*\(\s*['"]([^'"]+)['"]\s*\)/g },
      { method: 'PATCH', regex: /@Patch\s*\(\s*['"]([^'"]+)['"]\s*\)/g },
    ];

    for (const { method, regex } of nestJSPatterns) {
      let match;
      while ((match = regex.exec(code)) !== null) {
        const path = match[1];
        const lineNumber = code.substring(0, match.index).split('\n').length;

        endpoints.push({
          method,
          path,
          line_number: lineNumber,
          api_type: 'rest',
        });
      }
    }

    // GraphQL resolvers
    const graphqlQueryRegex = /@Query\s*\(\s*(?:['"]([^'"]+)['"])?\s*\)/g;
    const graphqlMutationRegex = /@Mutation\s*\(\s*(?:['"]([^'"]+)['"])?\s*\)/g;

    let match;
    while ((match = graphqlQueryRegex.exec(code)) !== null) {
      const operationName = match[1] || '<unnamed>';
      const lineNumber = code.substring(0, match.index).split('\n').length;

      endpoints.push({
        method: 'QUERY',
        path: operationName,
        line_number: lineNumber,
        api_type: 'graphql',
      });
    }

    while ((match = graphqlMutationRegex.exec(code)) !== null) {
      const operationName = match[1] || '<unnamed>';
      const lineNumber = code.substring(0, match.index).split('\n').length;

      endpoints.push({
        method: 'MUTATION',
        path: operationName,
        line_number: lineNumber,
        api_type: 'graphql',
      });
    }

    // gRPC service definitions
    const grpcServiceRegex = /service\s+(\w+)\s*{/g;
    const grpcRpcRegex = /rpc\s+(\w+)/g;

    while ((match = grpcServiceRegex.exec(code)) !== null) {
      const serviceName = match[1];
      const lineNumber = code.substring(0, match.index).split('\n').length;

      // Find all rpc methods in this service
      const serviceBlock = code.substring(match.index, match.index + 500); // Approximate
      let rpcMatch;
      while ((rpcMatch = grpcRpcRegex.exec(serviceBlock)) !== null) {
        const rpcName = rpcMatch[1];

        endpoints.push({
          method: 'RPC',
          path: `${serviceName}.${rpcName}`,
          line_number: lineNumber,
          api_type: 'grpc',
        });
      }
    }

    return endpoints;
  };
}

/**
 * Extract metadata from parsed code (convenience function)
 *
 * @param parseResult - Parse result with nodes, imports, exports
 * @param code - Raw code content
 * @returns Chunk metadata
 */
export const extractMetadata = (parseResult: ParseResult, code: string): ChunkMetadata => {
  const extractor = new MetadataExtractor();
  return extractor.extractMetadata(parseResult, code);
};

/**
 * Classify import path as internal (workspace/relative) or external (npm package)
 *
 * Helper function for monorepo import analysis that determines whether an import
 * is internal to the project (workspace reference or relative path) or external
 * (npm package from node_modules). Useful for:
 * - Detecting monorepo workspace dependencies
 * - Distinguishing between project code and third-party libraries
 * - Import chain expansion boundary detection
 *
 * Classification rules:
 * - Workspace patterns (e.g., @workspace/*, ~/*, #*) → internal
 * - Relative paths (./, ../) → internal
 * - Everything else → external (npm packages)
 *
 * @param importPath - Import source path from import statement
 * @returns true if internal workspace/relative import, false if external npm package
 */
export const classifyImport = (importPath: string): boolean => {
  // Check workspace patterns
  if (WORKSPACE_IMPORT_PATTERNS.some((pattern) => pattern.test(importPath))) {
    return true;
  }

  // Relative imports are internal
  if (importPath.startsWith('./') || importPath.startsWith('../')) {
    return true;
  }

  // Everything else is external (node_modules)
  return false;
};

/**
 * Calculate cyclomatic complexity from code string
 *
 * Computes the cyclomatic complexity metric by counting decision points in code.
 * This metric indicates code complexity and testing requirements - higher values
 * suggest more complex logic requiring more test cases.
 *
 * Formula: Complexity = decision points + 1
 *
 * Counted decision points:
 * - Control flow: if, else, while, for, case
 * - Logical operators: && (AND), || (OR)
 * - Ternary operator: ?
 * - Error handling: catch
 *
 * Complexity interpretation:
 * - 1-5: Simple, easy to test
 * - 6-10: Moderate complexity
 * - 11-20: Complex, needs refactoring consideration
 * - 21+: Very complex, high maintenance cost
 *
 * @param code - Raw code content to analyze
 * @returns Cyclomatic complexity score (minimum 1)
 */
export const calculateComplexity = (code: string): number => {
  let complexity = 1;

  const patterns = [
    /\bif\s*\(/g,
    /\belse\b/g,
    /\bwhile\s*\(/g,
    /\bfor\s*\(/g,
    /\bcase\s+/g,
    /&&/g,
    /\|\|/g,
    /\?/g,
    /\bcatch\s*\(/g,
  ];

  for (const pattern of patterns) {
    const matches = code.match(pattern);
    if (matches) {
      complexity += matches.length;
    }
  }

  return complexity;
};
